import { useEffect, useSyncExternalStore } from 'react';

// Portal actions a keyboard shortcut can trigger (the super admin binds keys + who may use them).
export const SC_ACTIONS = [
  ['clear_selection', 'Clear selection', 'Untick every selected piece on the current screen'],
  ['clear_filters', 'Clear filters', 'Reset every filter and search on the current screen'],
  ['toggle_customer_filter', 'Show / hide Customer filter', 'Jewellery tags page'],
  ['toggle_all_filters', 'Show / hide all filters', 'Dates, karigar and customer filters on the jewellery tags page'],
  ['open_approvals', 'Open Approval Register', ''],
  ['open_sales', 'Open Sales register', ''],
  ['open_lists', 'Open Saved lists', ''],
  ['shortcut_menu', 'Show keyboard shortcuts', ''],
  ['toggle_sales_mode', 'Sales mode on / off', 'Shows sale prices and Mark as sold (users who can sell)'],
  ['print_selected', 'Batch print tags of the selection', 'Jewellery tags page and Approval Register'],
  ['excel_selected', 'Excel of the selection', 'Opens the Excel export for the selected pieces'],
  ['pdf_selected', 'PDF of the selection', 'Item list PDF / approval PDF'],
  ['images_selected', 'Image list of the selection', ''],
  ['go_home', 'Close and go to Jewellery Tags page', 'Closes any open register, list or dialog'],
];
// Selection actions press the matching button of the screen in front.
const PAGE_CLICK = {
  excel_selected: { tags: 'tagws-selection-excel', approvals: 'tagws-approvals-excel' },
  pdf_selected: { tags: 'tagws-selection-item-pdf', approvals: 'tagws-approvals-pdf' },
  images_selected: { tags: 'tagws-selection-image-pdf', approvals: 'tagws-approvals-image-list' },
  print_selected: { tags: 'tagws-selection-print' },
};
// Pages a shortcut can be limited to (it only fires there and is listed under that page).
export const SC_PAGES = [['all', 'Every page'], ['tags', 'Jewellery tags page'], ['approvals', 'Approval Register'], ['sales', 'Sales register']];
// Page buttons a shortcut can press: action 'click:<testid>'. If the user cannot see the button, nothing happens.
export const SC_BUTTONS = {
  tags: [['tagws-selection-print', 'Print selected tags'], ['tagws-selection-send-approval', 'Send selected on approval', 'approve'], ['tagws-selection-mark-sold', 'Mark selected as sold', 'sell'], ['tagws-selection-save-list', 'Save selection as list', 'edit'], ['tagws-selection-add-to-list', 'Add selection to a list', 'edit'], ['tagws-selection-approval-return', 'Approval return (selected)', 'approve'], ['tagws-selection-approval-sell', 'Approval to sale (selected)', 'sell']],
  approvals: [['tagws-approvals-tab-customer', 'Customer approvals tab', 'register'], ['tagws-approvals-tab-marketing', 'Marketing tab', 'register'], ['tagws-approvals-return', 'Approval return', 'approve'], ['tagws-approvals-sell', 'Mark as sold', 'sell'], ['tagws-approvals-mk-send', 'Send marketing pieces on approval', 'approve'], ['tagws-approvals-excel', 'Excel', 'register'], ['tagws-approvals-pdf', 'PDF', 'register'], ['tagws-approvals-image-list', 'Image list', 'register'], ['tagws-approvals-close', 'Close the register', 'register']],
  sales: [['tagws-sales-tab-customers', 'Customers tab', 'sales'], ['tagws-sales-tab-sales', 'Sales persons tab', 'sales'], ['tagws-sales-tab-lists', 'Sold lists tab', 'sales'], ['tagws-sales-tab-returns', 'Returns tab', 'sales'], ['tagws-sales-tab-dash', 'Dashboard tab', 'sales'], ['tagws-sales-close', 'Close the register', 'sales']],
};
const ALL_BUTTONS = Object.values(SC_BUTTONS).flat();
// Feature access a shortcut needs, so nobody gets (or sees) keys for features they cannot use.
const ACCESS = {
  approve: (c) => !c.sales_only && (c.rates_edit || c.is_tag_super),
  sell: (c) => (c.can_sell && !c.sales_only) || c.is_tag_super,
  edit: (c) => !c.sales_only,
  register: (c) => !!c.approvals_access,
  sales: (c) => !!((c.can_sell && !c.sales_only) || c.sales_only || c.is_tag_super),
};
const ACTION_NEED = { open_approvals: 'register', open_sales: 'sales', toggle_sales_mode: 'sell' };
const needOf = (a) => (a.startsWith('click:') ? ALL_BUTTONS.find((x) => x[0] === a.slice(6))?.[2] : ACTION_NEED[a]);
// Buttons the built-in actions belong to (for tooltip hints).
const ACTION_BUTTON = { excel_selected: ['tagws-selection-excel', 'tagws-approvals-excel'], pdf_selected: ['tagws-selection-item-pdf', 'tagws-approvals-pdf'], images_selected: ['tagws-selection-image-pdf', 'tagws-approvals-image-list'], print_selected: ['tagws-selection-print'], clear_selection: ['tagws-selection-clear', 'tagws-approvals-clear'], open_approvals: ['tagws-nav-rail-approvals'], open_sales: ['tagws-nav-rail-sales'], shortcut_menu: ['tagws-nav-rail-shortcuts'] };
export const SC_WHO = [['everyone', 'Everyone'], ['sales', 'Sales persons'], ['rates', 'Rate editors'], ['sellers', 'Users who can sell'], ['super', 'Super admin only']];
// Built-in super-admin keys: never listed in the users' shortcut menu and cannot be re-bound.
export const SC_FIXED = [['Alt + D', 'Delete selected pieces (Alt + D D D restores)'], ['Alt + G, then H', 'Stock dashboard'], ['Ctrl + Z Z Z', 'Portal lock']];
export const SC_RESERVED = ['KeyD', 'KeyG', 'KeyH'];
// Built-in keys for every login, always listed in the shortcut menu.
export const SC_FIXED_ALL = [['Alt + G, then L', 'Log out of the portal']];
const mk = (id, action, code) => ({ id, action, code, alt: true, ctrl: false, shift: false, who: ['everyone'], on: true, page: 'all' });
export const DEFAULT_SHORTCUTS = [mk('sc1', 'clear_selection', 'KeyC'), mk('sc2', 'clear_filters', 'KeyF'), mk('sc3', 'toggle_customer_filter', 'KeyU'), mk('sc4', 'toggle_all_filters', 'KeyA'), mk('sc5', 'shortcut_menu', 'KeyK'), { ...mk('sc6', 'toggle_sales_mode', 'KeyS'), who: ['sellers'] }, mk('sc7', 'print_selected', 'KeyP'), mk('sc8', 'excel_selected', 'KeyE'), mk('sc9', 'go_home', 'KeyQ')];

let list = DEFAULT_SHORTCUTS;
const subs = new Set();
// Saved lists from before an action existed get its default shortcut added.
export function setShortcuts(v) {
  const added = ['toggle_sales_mode', 'print_selected', 'excel_selected', 'go_home'];
  list = Array.isArray(v) ? [...v, ...DEFAULT_SHORTCUTS.filter((d) => added.includes(d.action) && !v.some((s) => s.action === d.action || (s.code === d.code && !!s.alt === d.alt && !s.ctrl && !s.shift)))] : DEFAULT_SHORTCUTS;
  subs.forEach((f) => f());
}
export const useShortcuts = () => useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, () => list);

// Physical key of a key event. iPad / Mac Safari can send an empty code with Option held, so fall back
// to keyCode and then to the character Option types on an Apple keyboard (Option + B = ∫).
const OPT = 'å∫ç∂´ƒ©˙ˆ∆˚¬µ˜øπœ®ß†¨√∑≈¥Ω';
const OPT_DIGIT = 'º¡™£¢∞§¶•ª';
export function keyCodeOf(e) {
  if (/^(Key|Digit)/.test(e.code || '')) return e.code;
  const kc = e.keyCode || e.which;
  if (kc >= 65 && kc <= 90) return `Key${String.fromCharCode(kc)}`;
  if (kc >= 48 && kc <= 57) return `Digit${kc - 48}`;
  const k = String(e.key || '');
  if (/^[a-z]$/i.test(k)) return `Key${k.toUpperCase()}`;
  if (/^[0-9]$/.test(k)) return `Digit${k}`;
  const i = OPT.indexOf(k);
  if (i >= 0) return `Key${String.fromCharCode(65 + i)}`;
  const d = OPT_DIGIT.indexOf(k);
  if (d >= 0) return `Digit${d}`;
  return e.code || '';
}

export const keyLabel = (s) => [s.ctrl && 'Ctrl', s.alt && 'Alt', s.shift && 'Shift', s.code.replace(/^Key|^Digit/, '').replace('Slash', '/').replace('Period', '.').replace('Comma', ',')].filter(Boolean).join(' + ');
export const actionLabel = (a) => (a.startsWith('click:') ? ALL_BUTTONS.find((x) => x[0] === a.slice(6))?.[1] || a.slice(6) : SC_ACTIONS.find((x) => x[0] === a)?.[1] || a);
export const pageOf = (s) => s.page || 'all';
let page = 'tags';
// The screen in front (set by the portal page): tags | approvals | sales.
export const setScPage = (p) => { page = p; };
// Who may use a shortcut: super admin always; else by the picked groups.
export const canUse = (s, ctx) => !!ctx && s.on && (!needOf(s.action) || ACCESS[needOf(s.action)](ctx)) && (ctx.is_tag_super || s.who.includes('everyone') || (s.who.includes('sales') && ctx.is_sales) || (s.who.includes('rates') && ctx.rates_edit) || (s.who.includes('sellers') && ctx.can_sell && !ctx.sales_only));
// Shortcuts listed in the users' menu (super-admin-only ones stay out).
export const menuShortcuts = (all, ctx) => all.filter((s) => canUse(s, ctx) && !(s.who.length === 1 && s.who[0] === 'super'));

const EVT = 'tagws-action';
// One key listener for the portal: a matching shortcut fires its action to every screen.
export function useShortcutKeys(ctx) {
  const all = useShortcuts();
  useEffect(() => {
    const onKey = (e) => {
      const code = keyCodeOf(e);
      const hits = all.filter((x) => x.code === code && !!x.alt === e.altKey && !!x.ctrl === e.ctrlKey && !!x.shift === e.shiftKey && !e.metaKey && canUse(x, ctx) && (pageOf(x) === 'all' || pageOf(x) === page));
      const s = hits.find((x) => pageOf(x) === page) || hits[0];
      if (!s) return;
      e.preventDefault();
      runAction(s.action);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [all, ctx]);
}
// Run an action exactly as its keyboard shortcut would (also used by the touch Assistive button).
export function runAction(action) {
  const target = action.startsWith('click:') ? action.slice(6) : PAGE_CLICK[action]?.[page];
  if (target) {
    const b = [...document.querySelectorAll(`[data-testid="${target}"]`)].find((el) => el.offsetParent !== null);
    if (b && !b.disabled) b.click();
    return;
  }
  window.dispatchEvent(new CustomEvent(EVT, { detail: action }));
}
// May this login use the action at all (feature access)?
export const actionAllowed = (action, ctx) => !!ctx && (!needOf(action) || ACCESS[needOf(action)](ctx));
// Run `fn` when the given action fires (a map of action -> handler).
export function useTagActions(map) {
  useEffect(() => {
    const on = (e) => map[e.detail]?.();
    window.addEventListener(EVT, on);
    return () => window.removeEventListener(EVT, on);
  });
}

// Tooltip hints: buttons get "(Alt + X)" for the shortcuts this login may use (nothing for others).
export function useShortcutHints(ctx) {
  const all = useShortcuts();
  useEffect(() => {
    const mine = all.filter((s) => canUse(s, ctx));
    const map = new Map();
    const base = new Map();
    mine.forEach((s) => (s.action.startsWith('click:') ? [s.action.slice(6)] : ACTION_BUTTON[s.action] || []).forEach((id) => { map.set(id, [...(map.get(id) || []), keyLabel(s)]); base.set(id, actionLabel(s.action)); }));
    let raf = 0;
    const apply = () => {
      raf = 0;
      document.querySelectorAll('[data-sc-hint]').forEach((el) => { if (!map.has(el.dataset.testid)) { el.title = el.dataset.scBase || ''; delete el.dataset.scHint; } });
      map.forEach((keys, id) => document.querySelectorAll(`[data-testid="${id}"]`).forEach((el) => {
        const hint = keys.join(' / ');
        if (el.dataset.scHint === hint) return;
        if (el.dataset.scHint === undefined) el.dataset.scBase = el.title || el.getAttribute('aria-label') || base.get(id);
        el.dataset.scHint = hint; el.title = `${el.dataset.scBase} (${hint})`;
      }));
    };
    apply();
    const mo = new MutationObserver(() => { if (!raf) raf = requestAnimationFrame(apply); });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => { mo.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, [all, ctx]);
}
